import { Button, Callout, Dialog, DialogCancel, Textarea } from '@fruuit/ui'
import { type FormEvent, useEffect, useState } from 'react'
import { errorMessage, isApiError } from '../../api/client'

export interface ReasonDialogProps {
  open: boolean
  onClose: () => void
  title: string
  description?: string
  label: string
  hint?: string
  submitLabel: string
  /** Optional notes (submit) may be empty; reasons need 8+ characters (WF-006). */
  optional?: boolean
  danger?: boolean
  onSubmit: (reason: string) => Promise<unknown>
}

const MIN = 8
const MAX = 2000

/** One dialog for every "record why" step: cancel, approve, publish, return, submit. */
export function ReasonDialog({
  open,
  onClose,
  title,
  description,
  label,
  hint,
  submitLabel,
  optional = false,
  danger = false,
  onSubmit,
}: ReasonDialogProps) {
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  useEffect(() => {
    if (open) {
      setReason('')
      setError(null)
      setFailure(null)
    }
  }, [open])

  async function submit(event: FormEvent) {
    event.preventDefault()
    const trimmed = reason.trim()
    if (!optional && trimmed.length < MIN) {
      setError(`Record a reason of at least ${MIN} characters.`)
      return
    }
    setError(null)
    setFailure(null)
    setPending(true)
    try {
      await onSubmit(trimmed)
      onClose()
    } catch (err) {
      if (isApiError(err) && err.fieldErrors.reason) setError(err.fieldErrors.reason)
      else setFailure(errorMessage(err))
    } finally {
      setPending(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      size="sm"
      footer={
        <>
          <DialogCancel />
          <Button
            type="submit"
            form="reason-dialog-form"
            variant={danger ? 'danger' : 'primary'}
            loading={pending}
          >
            {submitLabel}
          </Button>
        </>
      }
    >
      <form id="reason-dialog-form" onSubmit={submit} className="flex flex-col gap-4" noValidate>
        {failure ? (
          <Callout tone="danger" role="alert">
            {failure}
          </Callout>
        ) : null}
        <Textarea
          label={label}
          hint={
            hint ??
            (optional ? 'Optional.' : `At least ${MIN} characters. Kept in the audit trail.`)
          }
          required={!optional}
          value={reason}
          maxLength={MAX}
          rows={4}
          error={error ?? undefined}
          onChange={(e) => setReason(e.target.value)}
        />
      </form>
    </Dialog>
  )
}
